import React from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

function formatSimTime(ms: number): string {
  const totalMinutes = Math.floor(Math.max(0, ms) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `T+${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

// Horizontal timeline across the exercise's full act span (plan 03). Runs at
// h:1 in the tabletop-soc preset, so the playhead readout is an absolute
// overlay above the rail rather than a separately stacked line — there is no
// vertical room in a single grid row for three stacked bands.
const ActRailWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const tabletop = state?.tabletop;

  if (!tabletop || tabletop.acts.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No acts authored
      </div>
    );
  }

  const acts = [...tabletop.acts].sort((a, b) => a.simTimeStartMs - b.simTimeStartMs);
  const railStartMs = acts[0].simTimeStartMs;
  const railEndMs = acts[acts.length - 1].simTimeEndMs;
  const span = Math.max(1, railEndMs - railStartMs);
  const playheadPct = Math.min(100, Math.max(0, ((tabletop.simTimeMs - railStartMs) / span) * 100));
  const nextAct = acts.find((a) => a.simTimeStartMs > tabletop.simTimeMs);

  return (
    <div className={`flex items-center h-full px-3 gap-3 transition-all ${tabletop.held ? 'grayscale opacity-50' : ''}`}>
      <div className="relative flex-1 pt-7 min-w-0">
        {/* Playhead readout — floats above the rail at the current sim-time position */}
        <div
          className="absolute top-0 flex flex-col items-center pointer-events-none"
          style={{ left: `${playheadPct}%`, transform: 'translateX(-50%)' }}
        >
          <span className={`text-[32px] leading-none font-mono font-bold whitespace-nowrap ${isDark ? 'text-white' : 'text-navy'}`}>
            {formatSimTime(tabletop.simTimeMs)}
          </span>
        </div>

        {/* Rail segments */}
        <div className="relative flex items-stretch gap-0.5 h-9">
          {acts.map((act) => {
            const width = ((act.simTimeEndMs - act.simTimeStartMs) / span) * 100;
            const isCurrent = act.actId === tabletop.currentActId;
            const isDone = act.simTimeEndMs <= tabletop.simTimeMs;
            return (
              <div
                key={act.actId}
                className={`flex items-center justify-center rounded-sm border text-lg font-semibold truncate px-1 ${
                  isCurrent
                    ? 'bg-primary border-primary text-white'
                    : isDone
                    ? isDark ? 'bg-white/15 border-white/10 text-white/50' : 'bg-slate-300 border-slate-300 text-slate-500'
                    : isDark ? 'bg-transparent border-white/20 text-white/40' : 'bg-transparent border-slate-300 text-slate-400'
                }`}
                style={{
                  width: `${width}%`,
                  animation: isCurrent && !tabletop.held ? 'act-playhead-pulse 2s ease-in-out infinite' : undefined,
                }}
              >
                {act.label}
              </div>
            );
          })}
          <div className="absolute top-0 bottom-0 w-0.5 bg-primary" style={{ left: `${playheadPct}%` }} />
        </div>
      </div>

      {/* Hold state / next-deadline countdown — replaced by the nearest open
          decision deadline once plan 04 supplies real deadlines; act
          boundaries are the honest approximation until then. */}
      {tabletop.held ? (
        <span className="flex-shrink-0 text-xl font-black tracking-widest text-amber-400">HELD</span>
      ) : nextAct ? (
        <span className={`flex-shrink-0 text-sm font-semibold whitespace-nowrap ${isDark ? 'text-amber-300/80' : 'text-amber-600'}`}>
          {nextAct.label} in {formatCountdown(nextAct.simTimeStartMs - tabletop.simTimeMs)}
        </span>
      ) : null}
    </div>
  );
};

export default ActRailWidget;
